import { useCallback, useEffect, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { styled, css } from 'styled-components'

import { useDebounceValue } from 'peach/hooks'

import { Select, Skeleton, TextInput } from 'core/components'
import DialogComponent from 'core/components/lib/Dialog/Dialog'
import {
  InternalBorrowerSearchVariables,
  InternalEmployeesGetVariables,
} from 'core/types/operations'

import useBorrowerAppSearch from '../AppSearch/pages/useBorrowerAppSearch'
import useEmployeeAppSearch from '../AppSearch/pages/useEmployeeAppSearch'
import SearchResults from '../AppSearch/SearchResults'

const defaultBorrowerQuery = {
  _hasTheWords: '',
}

const defaultEmployeeQuery = {
  search: '',
  limit: 25,
  offset: 0,
}

const Search = () => {
  const { pathname } = window.location
  const companyId = pathname.match(/\/companies\/(CP-[A-Z0-9]+-[A-Z0-9]+)/)?.[1]
  const queryClient = useQueryClient()

  const inputRef = useRef<HTMLInputElement>(null)

  const [isOpen, setIsOpen] = useState(false)
  const [queryType, setQueryType] = useState<'borrower' | 'employee'>(
    'borrower',
  )
  const [searchString, setSearchString] = useState('')
  const [debouncedSearchString] = useDebounceValue(searchString, 300)
  const [borrowerQuery, setBorrowerQuery] =
    useState<NonNullable<InternalBorrowerSearchVariables['query']>>(
      defaultBorrowerQuery,
    )
  const [employeeQuery, setEmployeeQuery] =
    useState<NonNullable<InternalEmployeesGetVariables['query']>>(
      defaultEmployeeQuery,
    )

  const { options: borrowerOptions, isLoading: isLoadingBorrowers } =
    useBorrowerAppSearch({
      query:
        isOpen && queryType === 'borrower'
          ? borrowerQuery
          : { _hasTheWords: '' },
      companyId,
      options: {
        enabled:
          isOpen &&
          queryType === 'borrower' &&
          !!companyId &&
          !!debouncedSearchString?.trim(),
      },
    })

  const {
    options: employeeOptions,
    isLoading: isLoadingEmployees,
    pagination: { total: count } = {},
  } = useEmployeeAppSearch({
    query: isOpen && queryType === 'employee' ? employeeQuery : { search: '' },
    companyId,
    options: {
      enabled:
        isOpen &&
        queryType === 'employee' &&
        !!companyId &&
        !!debouncedSearchString?.trim(),
    },
  })

  const isLoading = isLoadingBorrowers || isLoadingEmployees

  // Update search queries when the search string changes
  useEffect(() => {
    if (queryType === 'borrower') {
      setBorrowerQuery({
        ...defaultBorrowerQuery,
        _hasTheWords: debouncedSearchString,
      })
    } else if (queryType === 'employee') {
      setEmployeeQuery({
        ...defaultEmployeeQuery,
        search: debouncedSearchString,
      })
    }
  }, [debouncedSearchString, queryType])

  // Clear search queries when the search is closed
  useEffect(() => {
    if (isOpen) {
      setSearchString('')
      setTimeout(() => {
        inputRef.current?.focus()
      }, 10)
    } else {
      setQueryType('borrower')
      // Clear specific search queries by their exact keys
      queryClient.removeQueries({
        queryKey: ['companies', companyId, 'people', 'search'],
        exact: false, // This clears all search queries for this company
      })

      queryClient.removeQueries({
        queryKey: ['companies', companyId, 'employees'],
        exact: false, // This clears all employee queries for this company
      })
    }
  }, [isOpen, queryClient, companyId])

  // Keybindings for opening and closing the search
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setIsOpen(true)
      } else if (event.key === 'Escape' && isOpen) {
        event.preventDefault()
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  const onDone = useCallback(() => {
    setTimeout(() => {
      setSearchString('')
      setIsOpen(false)
    }, 100)
  }, [])

  return (
    <>
      <StyledTriggerInput
        onClick={() => setIsOpen(true)}
        placeholder='Search (⌘K)'
        value=''
        variant='outline'
      />

      <DialogComponent
        content={
          !companyId ? (
            <SearchUnavailable>
              Navigate to a company page or visit the companies list view to
              select a company.
            </SearchUnavailable>
          ) : (
            <>
              <Box>
                <StyledSelect
                  onChange={(e) => {
                    setQueryType(e as 'borrower' | 'employee')
                  }}
                  options={[
                    { label: 'Borrower', value: 'borrower' },
                    { label: 'Employee', value: 'employee' },
                  ]}
                  placeholder='Select'
                  value={queryType}
                />
                <StyledInput
                  onChange={(e) => setSearchString(e)}
                  placeholder={`Search ${
                    queryType === 'borrower' ? 'borrower' : 'employee'
                  }`}
                  ref={inputRef}
                  value={searchString}
                  variant='outline'
                />
              </Box>

              <ContentContainer>
                {isLoading && debouncedSearchString.trim() ? (
                  <Skeleton height='200px' width='100%'>
                    {/* Group Label */}
                    <rect height='12' width='80' x='8' y='8' />

                    {/* Result Item 1 */}
                    <rect height='16' width='220' x='8' y='32' />
                    <rect height='12' width='120' x='8' y='52' />

                    {/* Result Item 2 */}
                    <rect height='16' width='200' x='8' y='76' />
                    <rect height='12' width='100' x='8' y='96' />

                    {/* Result Item 3 */}
                    <rect height='16' width='240' x='8' y='120' />
                    <rect height='12' width='140' x='8' y='140' />

                    {/* Result Item 4 */}
                    <rect height='16' width='180' x='8' y='164' />
                    <rect height='12' width='90' x='8' y='184' />
                  </Skeleton>
                ) : (
                  <>
                    {queryType === 'borrower' &&
                    !!debouncedSearchString &&
                    borrowerOptions.length > 0 ? (
                      <SearchResults
                        onClick={onDone}
                        results={borrowerOptions}
                      />
                    ) : queryType === 'employee' &&
                      !!debouncedSearchString &&
                      employeeOptions.length > 0 ? (
                      <SearchResults
                        limit={employeeQuery.limit}
                        offset={employeeQuery.offset}
                        onClick={onDone}
                        onPaginationChange={(offset) =>
                          setEmployeeQuery((prev) => ({
                            ...prev,
                            offset,
                          }))
                        }
                        queryType={queryType}
                        results={employeeOptions}
                        total={count}
                      />
                    ) : debouncedSearchString.trim() ? (
                      <NoResults>No results</NoResults>
                    ) : null}
                  </>
                )}
              </ContentContainer>
            </>
          )
        }
        heightContainer='large'
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        title={companyId ? 'Search' : 'Search requires a company selection'}
        width='medium'
      />
    </>
  )
}

export default Search

const Box = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  padding: 10px;
`

const StyledSelect = styled(Select)`
  width: 136px;
`

const inputStyles = css`
  background-color: ${({ theme }) => theme.background};
  height: 40px;
  font-size: 14px;
`

const StyledTriggerInput = styled(TextInput)`
  ${inputStyles}
  cursor: pointer;
`

const StyledInput = styled(TextInput)`
  ${inputStyles}
  flex: 1;
`

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.text};
`

const ContentContainer = styled.div`
  min-height: 56px;
`

const SearchUnavailable = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px 4px;
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.text};

  h3 {
    opacity: 0.7;
    margin-bottom: 16px;
    color: ${({ theme }) => theme.disabled};
  }
`
